import { ContractInformation } from 'api/v1/information'
import {
  MarketIndexes,
  MarketPriceConfigResponse,
  MarketPriceResponse,
  MarketTrades,
} from 'api/v1/market'
import { PRIMARY_CURRENCY, PRIMARY_CURRENCY_PRICE } from 'constants/currencies'
import { useCallback, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { RootState, store, useAppDispatch } from 'store'
import { useCurrentPerpSymbol } from 'store/perp/hooks'
import { useCurrentSpotSymbol } from 'store/spot/hooks'
import { L2ChainId, SymbolId, SymbolName, TokenSymbol } from 'types'
import { useInterval } from 'usehooks-ts'
import { Equal } from '../../utils/equal'
import { getMarketPriceAction } from './actions/getMarketPriceActions'
import { MarketBarData, MarketPrice, Tickers, TradeType } from './types'

export function useCurrentSymbol() {
  const currentSpotSymbol = useCurrentSpotSymbol()
  const currentPerpSymbol = useCurrentPerpSymbol()
  const tradeType = useMarketTradeType()
  return useMemo(() => {
    return tradeType === TradeType.Perp && currentPerpSymbol.id
      ? currentPerpSymbol
      : tradeType === TradeType.Spot && currentSpotSymbol.id
        ? currentSpotSymbol
        : {}
  }, [currentSpotSymbol, currentPerpSymbol])
}
export function useMarketTradeType() {
  return useSelector<RootState, TradeType | ''>((state) => state.market.trade)
}
export function useMarketTrades() {
  return useSelector<RootState, MarketTrades>((state) => state.market.trades)
}
export function useMarketIndexes() {
  return useSelector<RootState, MarketIndexes>((state) => state.market.indexes)
}

export function useMarketBar() {
  return useSelector<RootState, MarketBarData>(
    (state) => state.market.marketBar,
  )
}
export function useCurrencyClosePrice(currency: TokenSymbol) {
  const tickers = useAllTicker()
  return useMemo(() => {
    if (!currency) return 0
    if (currency === PRIMARY_CURRENCY) {
      return PRIMARY_CURRENCY_PRICE
    }
    if (tickers[currency.toUpperCase() + '_' + PRIMARY_CURRENCY]) {
      return tickers[currency.toUpperCase() + '_' + PRIMARY_CURRENCY].close
    } else {
      return 0
    }
  }, [tickers, currency])
}

export function useAllTicker() {
  return useSelector<RootState, Tickers>((state) => state.market.tickers)
}
export function useTicker(symbol: SymbolName) {
  const tickers = useAllTicker()
  return tickers[symbol]
}

export function useCurrencies() {
  const trades = useMarketTrades()
  return trades.currencies
}

export function useCurrencyById(params: { id?: number; l2Id?: number }) {
  const currencies = useCurrencies()
  return useMemo(() => {
    if (params.id) return currencies.find((v) => v.id === params.id)
    if (params.l2Id)
      return currencies.find((v) => v.l2CurrencyId === params.l2Id)
    return undefined
  }, [currencies, params])
}

export function useCurrencyByName(name: TokenSymbol) {
  const currencies = useCurrencies()
  return useMemo(() => {
    return currencies.find((v) => Equal.upperCase(v.name, name))
  }, [currencies, name])
}

export function useSpotCurrencies(l2ChainId?: L2ChainId) {
  const currencies = useCurrencies()
  return useMemo(() => {
    const validCurrencies = currencies.filter((v) => v.valid)
    if (l2ChainId === undefined) {
      return validCurrencies
    } else {
      return validCurrencies.filter(
        (v) => v.chains && v.chains[l2ChainId] !== undefined,
      )
    }
  }, [currencies, l2ChainId])
}

export function useFetchMarketPrice(tradeType: TradeType, symbolId: SymbolId) {
  const dispatch = useAppDispatch()
  const getMarketPrice = useCallback(() => {
    if (!symbolId) return
    dispatch(
      getMarketPriceAction({
        tradeType,
        symbolId,
      }),
    )
  }, [tradeType, symbolId])

  useEffect(() => {
    getMarketPrice()
  }, [getMarketPrice])
  useInterval(() => {
    getMarketPrice()
  }, 5000)
}

export function useMarketPrice(symbolName: SymbolName): MarketPrice {
  const marketPrice = useSelector<RootState, MarketPriceResponse>(
    (state) => state.market.marketPrice,
  )
  return useMemo(() => {
    return {
      LONG: marketPrice.LONG[symbolName] ?? '0',
      SHORT: marketPrice.SHORT[symbolName] ?? '0',
    }
  }, [marketPrice, symbolName])
}

export function useMarketPriceConfig() {
  return useSelector<RootState, MarketPriceConfigResponse | undefined>(
    (state) => state.market.marketPriceConfig,
  )
}

export function useContractInformation() {
  return useSelector<RootState, Partial<ContractInformation>>(
    (state) => state.market.contractInformation,
  )
}

export function findCurrencyById(params: { id?: number; l2Id?: number }) {
  let currencies = store.getState()?.market.trades.currencies
  const { id, l2Id } = params
  if (id) {
    return currencies.find((item) => item.id === id)
  }
  if (l2Id) {
    return currencies.find((item) => item.l2CurrencyId === l2Id)
  }
  return undefined
}

export function useExternalPrices() {
  return useSelector<RootState, Record<SymbolName, number>>(
    (state) => state.market.externalPrices,
  )
}

export function useExternalPriceByName(symbolName: SymbolName) {
  return useSelector<RootState, number>(
    (state) => state.market.externalPrices[symbolName] ?? 0,
  )
}
